import React, { createRef, useEffect, useImperativeHandle } from 'react'

import { Button } from '@/components/atoms/Button/Button.tsx'
import {
  Card,
  type CardData,
  type CardLabels,
  type CardProps,
  type CardTimeFormat,
} from '@/components/molecules/Card/Card.tsx'
import { ConfirmDialog, type ConfirmDialogLabels } from '@/components/molecules/ConfirmDialog/ConfirmDialog.tsx'
import { type TeamCarData } from '@/components/molecules/Status'

export type CardPendingTimeFormat = CardTimeFormat

export interface CardPendingLabels extends CardLabels {
  assignManually: string
  close: string
  selectAvailableTeam: string
  cancelConfirmDialog: ConfirmDialogLabels
  assignConfirmDialog: ConfirmDialogLabels
}

export interface CardPendingState extends Pick<CardProps, 'state' | 'size'> {
  selectedTeam?: TeamCarData & { id: string }
  highlighted?: boolean
}

export interface CardPendingData extends Omit<CardData, 'donation' | 'requestStatus'> {
  pendingEvent?: 'cancelRequest' | 'assignRequest'
}

export interface CardPendingAction {
  onCancelRequest: () => void
  onAssignManually: () => void
  onClose: () => void
  onAssignTeam: (teamId: string) => void
}

export interface CardPendingProps
  extends
    React.HtmlHTMLAttributes<HTMLDivElement>,
    CardPendingState,
    CardPendingData,
    CardPendingTimeFormat,
    CardPendingAction {
  labels: CardPendingLabels
}

export const CardPending = React.forwardRef<HTMLDivElement, CardPendingProps>(
  (
    {
      state,
      size,
      request,
      status,
      team,
      locationLabel,
      pendingEvent,
      selectedTeam,
      labels,
      highlighted,
      timeAgoFormatter,
      willExecuteInTimeAgoFormatter,
      onCancelRequest,
      onAssignManually,
      onClose,
      onAssignTeam,
      ...props
    },
    ref
  ) => {
    const cellRef = createRef<HTMLDivElement>()
    useImperativeHandle(ref, () => cellRef.current!, [cellRef])
    useEffect(() => {
      if (highlighted) {
        cellRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    }, [state, cellRef, highlighted])
    return (
      <Card
        className={highlighted ? 'animate-pulse-gray' : ''}
        state={state}
        size={size}
        labels={labels}
        request={request}
        requestStatus="pending"
        status={status}
        team={team}
        selectedTeam={selectedTeam}
        locationLabel={locationLabel}
        timeAgoFormatter={timeAgoFormatter}
        willExecuteInTimeAgoFormatter={willExecuteInTimeAgoFormatter}
        ref={ref}
        {...props}>
        {state === 'active' && (
          <>
            <ConfirmDialog
              variant="critical"
              labels={labels.cancelConfirmDialog}
              onConfirm={onCancelRequest}
              working={pendingEvent === 'cancelRequest'}
              disabled={!!pendingEvent && pendingEvent !== 'cancelRequest'}
            />
            <Button
              loading={pendingEvent === 'assignRequest'}
              disabled={!!pendingEvent && pendingEvent !== 'assignRequest'}
              onClick={(event) => {
                onAssignManually()
                event.stopPropagation()
              }}>
              {labels.assignManually}
            </Button>
          </>
        )}
        {state === 'selected' && (
          <>
            <Button
              onClick={(event) => {
                onClose()
                event.stopPropagation()
              }}>
              {labels.close}
            </Button>
            {!selectedTeam ? (
              <div className="text-right text-base font-bold text-black">{labels.selectAvailableTeam}</div>
            ) : (
              <ConfirmDialog
                labels={labels.assignConfirmDialog}
                onConfirm={() => {
                  onAssignTeam(selectedTeam?.id)
                }}
                working={pendingEvent === 'assignRequest'}
                disabled={!!pendingEvent && pendingEvent !== 'assignRequest'}
              />
            )}
          </>
        )}
      </Card>
    )
  }
)
